/*
 * เอกสาร → กรอกข้อมูลเอกสาร (D3, choices 28 ต.ค. 2569). One panel for the whole work:
 *   ภาษาเอกสาร        ไทย / English — the words of the template, the fields' values (dates in full, ค.ศ.), table
 *                     headings; words the person changed are kept and counted (src/docs/docFields.ts switchLang)
 *   บริษัท / แบรนด์    pick one (0029) → its name, address, phone … and logo go into the fields made for them; what
 *                     the profile does not have is said, and typed by hand (no profile at all: type them all)
 *   ช่องข้อมูล         one input per key (same key = same value on every page), checked before anything changes
 *                     (all or nothing), "ใส่ข้อมูลลงเอกสาร" = one undo step; required fields still empty are listed
 *   จัดการบริษัท       admins and members with the right "เทมเพลต": add / change / take away a company (its logo too)
 * Every change goes through history.rewritePages (all pages as saved JSON, the open one drawn again).
 */
import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import { EXTRA_PROPS } from '../editor/editor';
import type { History } from '../editor/history';
import {
  checkField, fieldList, fillFields, logoPlace, switchLang, thaiToday, valueProblem,
  type DocField, type FoundField, type Plain
} from '../docs/docFields';
import { checkDocProfile, DOC_LANG_TH, LOGO_URL, PROFILE_KEY_TH, profileValue, type DocLang, type DocProfileData, type DocProfileInput, type ProfileKey } from '../docs/docProfile';
import { checkTable } from '../table/tableModel';
import { loadTableFonts, tableJson } from '../table/tableFabric';
import { api, ApiError, type DocProfile } from '../sync/api';
import { $, $in } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';

export interface DocFillHooks {
  /** why the work cannot be changed now (someone else's work · a page loading), or null */
  cannotEdit: () => string | null;
}

const PICK_KEY = 'craft.docProfile';
const errText = (e: unknown) => e instanceof Error ? e.message : String(e);

/** a picture from the server, opened (null when it cannot be — said by the caller) */
function openPicture(url: string, waitMs = 30_000): Promise<HTMLImageElement | null> {
  return new Promise(ok => {
    const el = new Image();
    const stop = setTimeout(() => { el.src = ''; ok(null); }, waitMs);
    el.onload = () => { clearTimeout(stop); ok(el.naturalWidth > 0 ? el : null); };
    el.onerror = () => { clearTimeout(stop); ok(null); };
    el.src = url;
  });
}

export function initDocFill(ed: Editor, history: History, h: DocFillHooks) {
  const say = (t: string, ok = false) => { const m = $('dcMsg'); m.textContent = t; m.hidden = !t; m.classList.toggle('ok', ok); m.setAttribute('role', ok ? 'status' : 'alert'); };
  let fields: FoundField[] = [];
  let lang: DocLang = 'th';
  let profiles: DocProfile[] = [];
  let canManage = false;
  let busy = false;

  /** the work's pages as saved JSON (the open one read from the canvas first) */
  const pagesNow = () => history.allPageObjects<Plain>();

  /* ---------- the fields ---------- */

  function open() {
    $('dcMain').hidden = false; $('dcCo').hidden = true;
    say('');
    read();
    openSheet('shDoc');
    void loadProfiles();
  }

  /** the fields and language from the work (the inputs are made again only here — never while typing: BUG-20) */
  function read() {
    const pages = pagesNow();
    const list = fieldList(pages);
    fields = list.fields;
    lang = pages.flat().some(o => (o.field as DocField | undefined)?.l === 'en' || (o.i18n as { l?: string } | undefined)?.l === 'en') ? 'en' : 'th';
    $('dcEmpty').hidden = fields.length > 0;
    $('dcTop').hidden = $('dcApply').hidden = !fields.length;
    $('dcLang').querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.v === lang)));
    const box = $('dcFields');
    box.textContent = '';
    for (const { field: f } of fields) box.append(row(f));
    paintMissing();
    if (list.differ.length) say(`ช่อง ${list.differ.join(', ')} มีค่าต่างกันในแต่ละหน้า — กด "ใส่ข้อมูลลงเอกสาร" แล้วทุกหน้าจะใช้ค่าในแผงนี้`);
  }

  function row(f: DocField): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'dcfield';
    wrap.dataset.k = f.k;
    const id = `dcf_${f.k}`;
    const label = document.createElement('label');
    label.className = 'lbl'; label.htmlFor = id;
    label.textContent = f.label[lang] + (f.label[lang] !== f.label.th ? ` (${f.label.th})` : '') + (f.req ? ' *' : '');
    if (f.src) { const tag = document.createElement('small'); tag.className = 'dctag'; tag.textContent = 'ข้อมูลบริษัท'; label.append(' ', tag); }
    wrap.append(label);
    const v = f.v ?? '';
    if (f.type === 'logo') {
      const p = document.createElement('p'); p.className = 'hint';
      p.textContent = 'โลโก้: เลือกบริษัทด้านบนแล้วกด "ใส่ข้อมูลบริษัท" (หรือแตะช่องโลโก้บนหน้าแล้วกด "ใส่รูป")';
      wrap.append(p);
      return wrap;
    }
    let input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (f.type === 'multiline') { input = document.createElement('textarea'); (input as HTMLTextAreaElement).rows = 3; }
    else if (f.type === 'choice') {
      const sel = document.createElement('select');
      sel.append(new Option('— เลือก —', ''));
      f.opts![lang].forEach((o, i) => sel.append(new Option(o, String(i))));
      input = sel;
    } else {
      input = document.createElement('input');
      input.type = f.type === 'date' ? 'date' : 'text';
      if (f.type === 'number') input.inputMode = 'decimal';
    }
    input.id = id; input.value = v; input.dataset.k = f.k;
    wrap.append(input);
    if (f.type === 'date') {
      const today = document.createElement('button');
      today.type = 'button'; today.className = 'btn dctoday'; today.textContent = 'วันนี้';
      today.onclick = () => { (input as HTMLInputElement).value = thaiToday(); };
      wrap.append(today);
    }
    return wrap;
  }

  /** what the inputs hold now, by key (logo: none) */
  function inputValues(): Record<string, string> {
    const out: Record<string, string> = {};
    $('dcFields').querySelectorAll<HTMLInputElement>('[data-k]:is(input, textarea, select)').forEach(el => {
      const f = fields.find(x => x.field.k === el.dataset.k)?.field;
      if (f) out[f.k] = f.type === 'multiline' ? el.value.replace(/\r\n?/g, '\n').trim() : el.value.trim();
    });
    return out;
  }
  /** only what differs from the work (one undo step for what really changed) */
  function changedValues(): Record<string, string> {
    const now = inputValues(), out: Record<string, string> = {};
    for (const { field: f } of fields) if (f.k in now && now[f.k] !== (f.v ?? '')) out[f.k] = now[f.k];
    return out;
  }
  /** every value's problem (all of them, so the person fixes them in one go) */
  function problems(values: Record<string, string>): string[] {
    const out: string[] = [];
    for (const [k, v] of Object.entries(values)) {
      const f = fields.find(x => x.field.k === k)?.field;
      const why = f ? valueProblem(f, v) : `ไม่พบช่อง ${k}`;
      if (why) out.push(why);
    }
    return out;
  }
  function paintMissing() {
    const now = inputValues();
    const empty = fields.filter(({ field: f }) => f.req && f.type !== 'logo' && !(now[f.k] ?? f.v ?? '')).map(({ field: f }) => f.label[lang]);
    const m = $('dcMissing');
    m.hidden = !empty.length;
    m.textContent = empty.length ? `ยังไม่ได้กรอก ${empty.length} ช่องที่จำเป็น: ${empty.join(', ')}` : '';
  }
  $('dcFields').addEventListener('change', paintMissing);

  /** put values (and maybe another language) into every page — one undo step; tables on other pages drawn again */
  async function put(values: Record<string, string>, to: DocLang | null): Promise<string | null> {
    const why = h.cannotEdit();
    if (why) return why;
    const before = JSON.stringify(pagesNow());
    const plan = (pages: Plain[][]) => {
      let p = pages, changed = 0, kept = 0;
      if (Object.keys(values).length) {
        const f = fillFields(p, values);
        if (!f.ok) return f.error;
        p = f.value.pages; changed += f.value.changed;
      }
      let tables: Array<{ page: number; index: number }> = [];
      if (to) { const s = switchLang(p, to); p = s.pages; changed += s.changed; kept = s.kept; tables = s.tables; }
      return { pages: p, changed, kept, tables };
    };
    const first = plan(JSON.parse(before) as Plain[][]);
    if (typeof first === 'string') return first;
    // a table in another language is drawn again in the JSON: its font first (BUG-78: said when it cannot come)
    for (const t of first.tables) {
      const k = checkTable(first.pages[t.page][t.index].table);
      if (!k.ok) return `ตารางในหน้า ${t.page + 1}: ${k.error}`;
      const failed = await loadTableFonts(k.value);
      if (failed) return failed.replace('ตารางยังไม่ได้เปลี่ยน', 'ยังไม่ได้เปลี่ยนอะไร');
    }
    let result = first;
    const res = history.rewritePages(pages => {
      // the work changed while the fonts came (BUG-31): nothing is written over it
      if (JSON.stringify(pages) !== before) return 'งานถูกแก้ระหว่างเตรียมข้อมูล ยังไม่ได้เปลี่ยนอะไร กดอีกครั้ง';
      const p = plan(pages);
      if (typeof p === 'string') return p;
      for (const t of p.tables) {
        const o = p.pages[t.page][t.index], k = checkTable(o.table);
        if (!k.ok) return `ตารางในหน้า ${t.page + 1}: ${k.error}`;
        p.pages[t.page][t.index] = tableJson(o, k.value);
      }
      result = p;
      return p.pages;
    });
    if (res) return res;
    const parts = [`เปลี่ยน ${result.changed} จุด`];
    if (result.kept) parts.push(`${result.kept} จุดที่แก้เองไว้ไม่ได้เปลี่ยนภาษา (แก้เองได้ที่หน้างาน)`);
    say(parts.join(' · '), true);
    return null;
  }

  $('dcApply').onclick = () => void apply();
  async function apply() {
    if (busy) return;
    const values = changedValues();
    const bad = problems(values);
    if (bad.length) { say(`ยังไม่ได้ใส่ข้อมูล: ${bad.join(' · ')}`); return; }
    if (!Object.keys(values).length) { say('ข้อมูลในแผงเหมือนในเอกสารแล้ว ไม่มีอะไรต้องเปลี่ยน', true); paintMissing(); return; }
    busy = true;
    const why = await put(values, null);
    busy = false;
    if (why) { say(why); return; }
    const kept = $('dcMsg').textContent;
    read();
    say(kept ?? '', true);
  }

  $('dcLang').onclick = e => {
    const b = (e.target as Element).closest<HTMLElement>('button[data-v]');
    if (!b || busy) return;
    const to = b.dataset.v as DocLang;
    if (to === lang) return;
    void (async () => {
      const values = changedValues();
      // ข้อมูลบริษัท: a value still the chosen company's (in this language) becomes the company's in the other one
      const co = profiles.find(x => x.id === $<HTMLSelectElement>('dcProfile').value)?.data;
      if (co) {
        const now = inputValues();
        for (const { field: f } of fields) {
          if (!f.src) continue;
          const was = profileValue(co, f.src, lang), next = profileValue(co, f.src, to);
          if (was && next && now[f.k] === was && next !== (f.v ?? '')) values[f.k] = next;
        }
      }
      const bad = problems(values);
      if (bad.length) { say(`เปลี่ยนภาษาไม่ได้ เพราะข้อมูลในแผงยังไม่ถูก: ${bad.join(' · ')}`); return; }
      busy = true;
      const why = await put(values, to);
      busy = false;
      if (why) { say(why); return; }
      const msg = $('dcMsg').textContent ?? '';
      read();
      say(`เปลี่ยนเอกสารเป็นภาษา ${DOC_LANG_TH[to]} แล้ว · ${msg}`, true);
    })();
  };

  /* ---------- บริษัท / แบรนด์ ---------- */

  async function loadProfiles() {
    $('dcCoNote').textContent = 'กำลังโหลดข้อมูลบริษัท…';
    try {
      const r = await api.docProfiles();
      profiles = r.profiles; canManage = r.canManage;
    } catch (e) {
      profiles = []; canManage = false;
      $('dcCoNote').textContent = `โหลดข้อมูลบริษัทไม่ได้ (${errText(e)}) กรอกเองในช่องด้านล่างได้`;
      paintProfiles();
      return;
    }
    $('dcCoNote').textContent = '';
    paintProfiles();
  }
  function paintProfiles() {
    const sel = $<HTMLSelectElement>('dcProfile');
    const want = sel.value || (() => { try { return localStorage.getItem(PICK_KEY) ?? ''; } catch { return ''; } })();
    sel.textContent = '';
    if (!profiles.length) sel.append(new Option('— ยังไม่มีข้อมูลบริษัท —', ''));
    for (const p of profiles) sel.append(new Option(p.label, p.id));
    if (profiles.some(p => p.id === want)) sel.value = want;
    $<HTMLButtonElement>('dcFillCo').disabled = !profiles.length;
    if (!profiles.length && !$('dcCoNote').textContent) {
      $('dcCoNote').textContent = canManage ? 'ยังไม่มีข้อมูลบริษัท — กด "จัดการข้อมูลบริษัท/แบรนด์" เพื่อเพิ่ม หรือกรอกเองในช่องด้านล่าง'
        : 'ยังไม่มีข้อมูลบริษัท — กรอกเองในช่องด้านล่าง (ผู้ดูแลหรือคนที่มีสิทธิ์ "เทมเพลต" เพิ่มให้ได้)';
    }
    $('dcManage').hidden = !canManage;
  }
  $<HTMLSelectElement>('dcProfile').onchange = () => { try { localStorage.setItem(PICK_KEY, $<HTMLSelectElement>('dcProfile').value); } catch { /* a convenience only */ } };

  $('dcFillCo').onclick = () => void fillCompany();
  async function fillCompany() {
    if (busy) return;
    const p = profiles.find(x => x.id === $<HTMLSelectElement>('dcProfile').value);
    if (!p) { say('เลือกบริษัท/แบรนด์ก่อน'); return; }
    if (!p.data) { say(`ข้อมูลของ "${p.label}" เสีย อ่านไม่ได้ ให้ผู้ดูแลบันทึกใหม่`); return; }
    const data: DocProfileData = p.data;
    // the company's values go into the inputs first: the person sees them and can change them before they go in
    const missing = new Set<string>();
    const values = inputValues();
    for (const { field: f } of fields) {
      if (!f.src) continue;
      const v = profileValue(data, f.src, lang);
      // a value the company does not have is emptied: another company's address must not stay under this one's name
      values[f.k] = v;
      if (!v) missing.add(PROFILE_KEY_TH[f.src as ProfileKey]);
    }
    $('dcFields').querySelectorAll<HTMLInputElement>('[data-k]:is(input, textarea, select)').forEach(el => { if (el.dataset.k! in values) el.value = values[el.dataset.k!]; });
    const changed = changedValues();
    const bad = problems(changed);
    if (bad.length) { say(`ข้อมูลบริษัทใส่ไม่ได้: ${bad.join(' · ')}`); return; }
    busy = true;
    try {
      const notes: string[] = [];
      const logos = fields.filter(x => x.field.type === 'logo');
      if (logos.length && !p.logo) notes.push(`"${p.label}" ยังไม่มีโลโก้ ช่องโลโก้ยังว่าง`);
      if (Object.keys(changed).length) {
        const why = await put(changed, null);
        if (why) { say(why); return; }
      }
      if (logos.length && p.logo) {
        const why = await putLogo(p);
        if (why) notes.push(why);
      }
      read();
      if (missing.size) notes.push(`"${p.label}" ไม่มี ${[...missing].join(', ')} — ช่องนั้นว่างไว้ กรอกเองได้`);
      say([`ใส่ข้อมูล "${p.label}" แล้ว`, ...notes].join(' · '), !notes.length);
    } finally { busy = false; }
  }

  /** the company's logo in every logo slot, fitted inside it (not cropped); the picture becomes mine first */
  async function putLogo(p: DocProfile): Promise<string | null> {
    let url: string;
    try { url = (await api.takeDocLogo(p.id)).url; } catch (e) { return `ใส่โลโก้ไม่ได้: ${errText(e)}`; }
    const el = await openPicture(url);
    if (!el) return `เปิดรูปโลโก้ของ "${p.label}" ไม่ได้ ช่องโลโก้ยังเหมือนเดิม`;
    const why = h.cannotEdit();
    if (why) return why;
    return history.rewritePages(pages => pages.map(objs => objs.map(o => {
      const k = o.field !== undefined ? checkField(o.field) : null;
      if (!k?.ok || k.value.type !== 'logo') return o;
      const w = k.value.box?.[0] ?? Number(o.width ?? 0) * Number(o.scaleX ?? 1), hgt = k.value.box?.[1] ?? Number(o.height ?? 0) * Number(o.scaleY ?? 1);
      const { left, top, scale: s } = logoPlace(o, [w, hgt], el.naturalWidth, el.naturalHeight);
      const img = new fabric.Image(el, { left, top, scaleX: s, scaleY: s });
      img.set({ name: `โลโก้ ${p.label}`.slice(0, 40) } as Partial<fabric.Image>);
      const json = img.toObject(EXTRA_PROPS) as Plain;
      // the slot's place is kept as the logo's box: another company's logo fits the same place later
      return { ...json, src: url, field: { ...k.value, box: [w, hgt] }, left, top };
    })));
  }

  /* ---------- จัดการข้อมูลบริษัท ---------- */

  let editing: DocProfile | null = null, logo: string | null = null;
  const coSay = (t: string, ok = false) => { const m = $('dcoMsg'); m.textContent = t; m.hidden = !t; m.classList.toggle('ok', ok); };

  $('dcManage').onclick = () => { $('dcMain').hidden = true; $('dcCo').hidden = false; $('dcoForm').hidden = true; paintList(); };
  $('dcoBack').onclick = () => { $('dcCo').hidden = true; $('dcMain').hidden = false; paintProfiles(); };

  function paintList() {
    const list = $('dcoList');
    list.textContent = '';
    if (!profiles.length) { const li = document.createElement('li'); li.className = 'hint'; li.textContent = 'ยังไม่มี — กด "เพิ่มบริษัท/แบรนด์"'; list.append(li); }
    for (const p of profiles) {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'rowlink';
      b.textContent = p.label + (p.data ? '' : ' (ข้อมูลเสีย)');
      const small = document.createElement('small'); small.textContent = p.logo ? 'มีโลโก้' : 'ไม่มีโลโก้';
      b.append(small);
      b.onclick = () => edit(p);
      li.append(b);
      list.append(li);
    }
  }
  const FORM: Array<[string, (d: DocProfileData) => string]> = [
    ['dcoThName', d => d.th.name], ['dcoThAddr', d => d.th.address], ['dcoEnName', d => d.en.name], ['dcoEnAddr', d => d.en.address],
    ['dcoPhone', d => d.phone], ['dcoEmail', d => d.email], ['dcoTax', d => d.taxId], ['dcoWeb', d => d.website]
  ];
  function edit(p: DocProfile | null) {
    editing = p; logo = p?.logo ?? null;
    $('dcoForm').hidden = false;
    $('dcoFormTitle').textContent = p ? `แก้ "${p.label}"` : 'เพิ่มบริษัท/แบรนด์';
    $in('dcoLabel').value = p?.label ?? '';
    const empty: DocProfileData = { th: { name: '', address: '' }, en: { name: '', address: '' }, phone: '', email: '', taxId: '', website: '' };
    for (const [id, get] of FORM) $<HTMLInputElement>(id).value = get(p?.data ?? empty);
    $('dcoDelete').hidden = !p;
    paintLogo();
    coSay('');
    $in('dcoLabel').focus();
  }
  function paintLogo() {
    const img = $<HTMLImageElement>('dcoLogoImg');
    img.hidden = !logo;
    if (logo) img.src = logo; else img.removeAttribute('src');
    $('dcoLogoNote').textContent = logo ? '' : 'ยังไม่มีโลโก้';
  }
  $('dcoAdd').onclick = () => edit(null);
  $('dcoCancel').onclick = () => { $('dcoForm').hidden = true; editing = null; };
  $('dcoLogoClear').onclick = () => { logo = null; paintLogo(); };
  $('dcoLogoPick').onclick = () => $<HTMLInputElement>('dcoLogoFile').click();
  $<HTMLInputElement>('dcoLogoFile').onchange = () => void (async () => {
    const input = $<HTMLInputElement>('dcoLogoFile'), f = input.files?.[0];
    input.value = '';
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) { coSay(`ไฟล์ "${f.name}" ไม่ใช่ PNG, JPG หรือ WebP`); return; }
    coSay('กำลังอัปโหลดโลโก้…', true);
    try {
      const { url } = await api.upload(f);
      if (!LOGO_URL.test(url)) { coSay('อัปโหลดแล้วแต่ที่อยู่รูปไม่ถูกรูปแบบ ลองใหม่'); return; }
      logo = url; paintLogo(); coSay('อัปโหลดโลโก้แล้ว กด "บันทึก" เพื่อใช้', true);
    } catch (e) { coSay(`อัปโหลดโลโก้ไม่สำเร็จ: ${errText(e)}`); }
  })();

  $('dcoSave').onclick = () => void (async () => {
    const input: DocProfileInput = {
      label: $in('dcoLabel').value,
      data: {
        th: { name: $in('dcoThName').value, address: $<HTMLTextAreaElement>('dcoThAddr').value },
        en: { name: $in('dcoEnName').value, address: $<HTMLTextAreaElement>('dcoEnAddr').value },
        phone: $in('dcoPhone').value, email: $in('dcoEmail').value, taxId: $in('dcoTax').value, website: $in('dcoWeb').value
      },
      logo
    };
    // the same checker as the server (BUG-13): said here, before sending
    const k = checkDocProfile(input);
    if (!k.ok) { coSay(k.error); return; }
    $<HTMLButtonElement>('dcoSave').disabled = true;
    try {
      if (editing) await api.saveDocProfile(editing.id, k.value, editing.updatedAt);
      else await api.addDocProfile(k.value);
      await loadProfiles();
      $('dcoForm').hidden = true; editing = null;
      paintList();
      toast(`บันทึก "${k.value.label}" แล้ว`);
    } catch (e) {
      coSay(`บันทึกไม่สำเร็จ: ${errText(e)}`);
      if (e instanceof ApiError && (e.status === 409 || e.status === 404)) { await loadProfiles(); paintList(); }
    } finally { $<HTMLButtonElement>('dcoSave').disabled = false; }
  })();

  $('dcoDelete').onclick = () => void (async () => {
    if (!editing) return;
    if (!confirm(`ลบ "${editing.label}"? เอกสารที่กรอกไปแล้วยังเหมือนเดิม (รวมโลโก้)`)) return;
    try {
      await api.deleteDocProfile(editing.id, editing.updatedAt);
      await loadProfiles();
      $('dcoForm').hidden = true; editing = null;
      paintList();
    } catch (e) {
      coSay(`ลบไม่สำเร็จ: ${errText(e)}`);
      if (e instanceof ApiError && (e.status === 409 || e.status === 404)) { await loadProfiles(); paintList(); }
    }
  })();

  /* ---------- ways in ---------- */

  $('dDoc').onclick = open;
  // the toolbar's "กรอกข้อมูล" on a field, and a document template just put in (src/ui/templatesSheet.ts)
  ed.canvas.on('doc:fill' as never, (() => open()) as never);

  return { open };
}
